import type { DtcCodeEntity } from '@packages/domain/dtc-codes';
import type { RetailerProfileEntity } from '@packages/domain/retailers';
import type { TechlineAssetEntity } from '@packages/domain/techline/assets';
import type { TechnicianAssignmentEntity } from '@packages/domain/techline/assignments';
import type { AllowCaseFeatures, FailCodeData, TechlineCaseEntity } from '@packages/domain/techline/cases';
import type { TechlineCommentEntity } from '@packages/domain/techline/comments';
import type { WorksheetSubmissionGroupEntity } from '@packages/domain/techline/worksheet-submissions';
import type { VinDetailEntity } from '@packages/domain/vin';

import { ANALYTICS_EVENTS } from '@packages/core/analytics';
import { colors } from '@packages/core/styles';
import { WorksheetAnswerStatus } from '@packages/models/api';
import { AlertBar, Button, Icon, Label, TextareaHelper, Typography } from '@packages/ui/shared';
import { WorksheetLayout } from '@web/components/worksheet-layout';
import { analyticsService } from '@web/services/singletons';
import React, { useCallback, useMemo, useState } from 'react';
import { Form, Modal, Spinner } from 'react-bootstrap';
import { useDropzone } from 'react-dropzone';
import { createUseStyles } from 'react-jss';
import { useNavigate } from 'react-router-dom';

import type { WorksheetOpenMode } from '../../worksheet-group-accordion/worksheet-group-accordion.types';
import type { WorksheetGroupWithTitles } from './submitted-case-subview.types';

import useUserDetailsOrPermissions from '../../../hooks/useUserDetailsOrPermissions';
import { signalDashboardAndCloseTab } from '../../../utils/case-tab-sync';
import { TechlineHeader } from '../../techline-header';
import { CaseTeamSection } from '../draft-case-subview/draft-sidebar/case-team-section';
import { CaseCommentList } from './case-comment-list';
import { AssignmentsSection } from './sections/assignments-section/assignments-section';
import { ConcernDetailsSection } from './sections/concern-details-section';
import { FailCodeSection } from './sections/fail-code-section/fail-code-section';
import { TechDescriptionSection } from './sections/tech-description-section/tech-description-section';
import { VehicleInfoSection } from './sections/vehicle-info-section';
import { WorksheetsSection } from './sections/worksheets-section/worksheets-section';

const CLOSE_CASE_TOOLTIP = 'Please complete all the required steps to close the case.';
const CLOSE_CASE_NOT_ALLOWED_TOOLTIP = 'This case is under review and will be closed by a Techline Rep once complete.';

/** Inline style for the comment section top border divider. */
const commentDividerStyle = { borderTop: `1px solid ${colors.grayThree}` };

/** Inline style for the dropzone paperclip icon offset. */
const dropzoneOffsetStyle = { marginBottom: -32, marginRight: 7 };

/** Inline style for the paperclip icon button. */
const paperclipStyle = { cursor: 'pointer', zIndex: 10, transform: 'rotate(90deg)' };

/** Inline style for the close description character count row. */
const closeDescriptionCountStyle = { display: 'flex', justifyContent: 'space-between' };

/**
 * Props for SubmittedCaseSubview.
 *
 * This is a presentational component that renders a submitted (non-draft)
 * techline case. All data flows down as props from the parent view, and
 * all user actions bubble up as `on*` callbacks. No services or API calls
 * are made directly by this component.
 */
export type SubmittedCaseSubviewProps = {
    // -- Data props --

    /** Whether specific features are allowed for the case. E.g., DTC codes, fail codes, additional worksheets, etc. */
    allowCaseFeatures: AllowCaseFeatures;

    /** Case-level file attachments (complete or still uploading). */
    assetData: TechlineAssetEntity[];

    /** Error message from a failed asset upload or processing. Empty when no error. */
    assetErrorMessage: string;

    /** Technician assignments for the case. */
    assignmentData: TechnicianAssignmentEntity[] | undefined;

    /** Whether the user can edit the submitted case. Users with the DSQM role cannot edit submitted cases. */
    canEditSubmittedCase: boolean;

    /** The full techline case data from NorthStar. */
    caseDetails: TechlineCaseEntity | undefined;

    /** The case number string. */
    caseNumber: string;

    /** Comments on the case. */
    commentData: TechlineCommentEntity[];

    /** Diagnostic Trouble Codes associated with the case. */
    dtcCodeData: DtcCodeEntity[];

    /** The current fail code data. Null when no fail code is set. */
    failCodeData: FailCodeData | null;

    /** Errors from fail code operations. */
    failCodeErrors: string[];

    /** True while a comment is being saved. */
    isCommentLoading: boolean;

    /** True while a fail code update is saving. */
    isFailCodeSaving: boolean;

    /** True while assignments are being fetched. */
    isLoadingAssignments: boolean;

    /** True while worksheet submission groups are being re-fetched. */
    isLoadingWorksheets: boolean;

    /** The primary retailer profile for this case. */
    primaryRetailerData?: RetailerProfileEntity;

    /** Whether the fail code modal is visible. */
    showFailCodeModal: boolean;

    /**
     * VIN details for the vehicle associated with the case.
     */
    vehicleVinData: VinDetailEntity | undefined;

    /** Worksheet submission groups, fetched and refreshed independently from case data. */
    worksheetGroupData: WorksheetSubmissionGroupEntity[] | null;

    /** Available worksheet titles for the add-worksheet modal. */
    worksheetTitleData: WorksheetGroupWithTitles[] | undefined;

    /** Map of worksheet ID to validation result (true = valid, false = missing required info). */
    worksheetValidationResults?: Record<string, boolean>;

    // -- Callback props --

    /** Add a comment to the case. */
    onAddComment: (message: string) => Promise<boolean>;

    /** Add a team member to the case by username. */
    onAddTeamMember: (username: string) => Promise<boolean>;

    /** Dismiss the asset error alert bar. */
    onAssetErrorDismiss: () => void;

    /** Clear the fail code from the case. */
    onClearFailCode: () => Promise<void>;

    /** Close the case with a description and vehicle repair status. */
    onCloseCase: (params: {
        /** The closing description text. */
        description: string;
        /** Whether the vehicle has been repaired. */
        isVehicleRepaired: boolean;
    }) => Promise<boolean>;

    /** Close the fail code modal. */
    onCloseFailCodeModal: () => void;

    /** Delete a case-level asset by ID. */
    onDeleteAsset: (assetId: string) => Promise<void>;

    /** Delete a draft worksheet submission by submission ID. */
    onDeleteDraftWorksheetSubmission: (submissionId: string) => Promise<boolean>;

    /** Fetch available worksheet titles for the add-worksheet modal. */
    onFetchWorksheetTitles: (params: {
        /** Whether the current user is a pilot user. */
        isPilotUser: boolean;
    }) => Promise<WorksheetGroupWithTitles[] | undefined>;

    /** Open the fail code modal. */
    onOpenFailCodeModal: () => void;

    /** Navigate to the worksheet editor for a given worksheet. */
    onOpenWorksheet: (worksheetId: string, submissionId: string | undefined, mode: WorksheetOpenMode) => void;

    /** Refresh assignments from the API. */
    onRefreshAssignments: () => Promise<void>;

    /** Refresh case-level assets from the API. */
    onRefreshAssets: () => Promise<void>;

    /** Refresh comments from the API. */
    onRefreshComments: () => Promise<void>;

    /** Toggle an assignment's completion status. */
    onToggleAssignmentCompletion: (assignmentId: string) => Promise<void>;

    /** Update a fail code on the case with the given data. */
    onUpdateFailCode: (data: FailCodeData) => Promise<void>;

    /** Update a case-level asset (replace blob and/or update metadata). */
    onUpdateAsset: (
        assetId: string,
        update: {
            /** Replacement file blob. */
            blob?: Blob;
            /** Updated asset name. */
            name?: string;
            /** Updated asset note. */
            note?: string;
        }
    ) => Promise<void>;

    /** Upload a new file as a case-level asset. */
    onUploadAsset: (file: File) => Promise<void>;
};

/**
 * Presentational component for viewing a submitted (non-draft) techline case.
 * Renders the case header, main content area (fail code, vehicle info, concern,
 * attachments, worksheets, assignments), and a comments sidebar.
 *
 * All data is received via props and all mutations are delegated to the parent
 * via callback props. The only local state is ephemeral UI state (modals,
 * section expansion, form inputs).
 *
 * @param props - {@link SubmittedCaseSubviewProps}
 * @returns the submitted case subview JSX
 */
export function SubmittedCaseSubview({
    allowCaseFeatures,
    assetData,
    assetErrorMessage,
    assignmentData,
    canEditSubmittedCase,
    caseDetails,
    caseNumber,
    commentData,
    dtcCodeData,
    failCodeData,
    failCodeErrors,
    isCommentLoading,
    isFailCodeSaving: _isFailCodeSaving,
    isLoadingAssignments,
    isLoadingWorksheets,
    onAddComment,
    onAddTeamMember,
    onAssetErrorDismiss,
    onClearFailCode,
    onCloseCase,
    onCloseFailCodeModal,
    onDeleteAsset,
    onDeleteDraftWorksheetSubmission,
    onFetchWorksheetTitles,
    onOpenFailCodeModal,
    onOpenWorksheet,
    onRefreshAssignments,
    onRefreshAssets,
    onRefreshComments: _onRefreshComments,
    onToggleAssignmentCompletion,
    onUpdateAsset,
    onUpdateFailCode,
    onUploadAsset,
    primaryRetailerData,
    showFailCodeModal,
    vehicleVinData,
    worksheetGroupData,
    worksheetTitleData,
    worksheetValidationResults = {},
}: SubmittedCaseSubviewProps) {
    // State
    const [closeDescription, setCloseDescription] = useState('');
    const [closeCaseLoading, setCloseCaseLoading] = useState(false);
    const [currentEditingComment, setCurrentEditingComment] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [isVehicleRepaired, setIsVehicleRepaired] = useState(true);
    const [showCloseCaseModal, setShowCloseCaseModal] = useState(false);
    const [sidebarTab, setSidebarTab] = useState<'comments' | 'details'>('comments');
    const [successMessage, setSuccessMessage] = useState('');

    const {
        allowAdditionalWorksheets,
        allowCloseCase,
        allowDtcCodes,
        allowFailCodes,
        allowCustomerConcernDetails,
        allowTechDescription,
    } = allowCaseFeatures || {};

    const containerRef = React.useRef<HTMLDivElement>(null);

    // Hooks
    const classes = useStyles();
    const navigate = useNavigate();

    const {
        isPilotUser,
        permissions: { canAddComment, canAddOrEditAttachment },
    } = useUserDetailsOrPermissions();

    // Forwarded callback aliases
    const handleAddTeamMember = onAddTeamMember;
    const handleRefreshAssignments = onRefreshAssignments;
    const handleToggleAssignmentCompletion = onToggleAssignmentCompletion;

    // Derived state

    /** Whether every worksheet submission in the case is in SUBMITTED status (no drafts remaining). */
    const hasAllWorksheetsSubmitted = useMemo(() => {
        if (!worksheetGroupData?.length) {
            return true;
        }
        return worksheetGroupData.every((group) =>
            group.worksheetSubmissionTitles.every((ws) => ws.status === WorksheetAnswerStatus.Submitted)
        );
    }, [worksheetGroupData]);

    /** Whether the case has a fail code set (required to close). */
    const hasFailCode = useMemo(() => {
        return Boolean(failCodeData?.repairCode && failCodeData?.section);
    }, [failCodeData]);

    /** The retailer number for the primary retailer on this case. */
    const retailerNumber = useMemo(() => {
        return caseDetails?.retailerProfiles?.find((retailer) => retailer.isPrimary)?.retailerNumber || '';
    }, [caseDetails?.retailerProfiles]);

    /** Whether the case is in a closed status. */
    const isCaseClosed = useMemo(() => {
        const closedStatuses = ['CLOSED-RESOLVED', 'CLOSE RESOLVED', 'CLOSED – AUTO CLOSED', 'AUTO CLOSED', 'CLOSED'];
        return closedStatuses.includes(caseDetails?.techlineStatusCode || '');
    }, [caseDetails?.techlineStatusCode]);

    /** Whether the close case button should display a tooltip, based on allowed features and case state. */
    const closeCaseTooltip = !allowCloseCase
        ? CLOSE_CASE_NOT_ALLOWED_TOOLTIP
        : !hasFailCode || !hasAllWorksheetsSubmitted
          ? CLOSE_CASE_TOOLTIP
          : undefined;

    // Handlers

    /**
     * Handle the cancel button click in the close-case modal.
     * Hides the modal and clears the description.
     */
    const handleCancelCloseCaseButtonClick = useCallback(() => {
        setShowCloseCaseModal(false);
        setCloseDescription('');
    }, []);

    /**
     * Handle closing the case. Calls the parent's onCloseCase callback,
     * then signals the dashboard and closes the tab on success.
     */
    const handleCloseCaseButtonClick = useCallback(async () => {
        try {
            setCloseCaseLoading(true);
            const success = await onCloseCase({
                description: closeDescription,
                isVehicleRepaired,
            });
            setShowCloseCaseModal(false);
            setCloseCaseLoading(false);
            if (success) {
                signalDashboardAndCloseTab({
                    retailerNumber: primaryRetailerData?.retailerNumber,
                    caseNumber,
                    fallbackNavigate: navigate,
                    queryParams: `showCloseCaseSuccessFor=${caseNumber}`,
                });
            }
        } catch (err) {
            alert('Error: ' + err + '. Please refresh & try again.');
            setCloseCaseLoading(false);
        }
    }, [caseNumber, closeDescription, isVehicleRepaired, navigate, onCloseCase, primaryRetailerData]);

    /**
     * Handle the close description text change, enforcing the 2000-character limit.
     *
     * @param text - the new text value
     */
    const handleCloseDescriptionChange = useCallback((text: string) => {
        if (text.length <= 2000) {
            setCloseDescription(text);
        } else {
            setCloseDescription(text.substring(0, 2000));
        }
    }, []);

    /**
     * Handle hiding the close-case modal.
     */
    const handleCloseCaseModalHide = useCallback(() => {
        setShowCloseCaseModal(false);
    }, []);

    /**
     * Handle a file drop in the comment attachment dropzone. Uploads the
     * file as a case-level asset and refreshes the asset list.
     *
     * @param acceptedFiles - the files accepted by the dropzone
     */
    const handleCommentAttachmentDrop = useCallback(
        async (acceptedFiles: File[]) => {
            try {
                if (acceptedFiles[0]) {
                    analyticsService.logEvent(ANALYTICS_EVENTS.USER_STARTS_ATTACHMENT_COMMENT);
                    await onUploadAsset(acceptedFiles[0]);
                    analyticsService.logEvent(ANALYTICS_EVENTS.USER_SUCCESSFULLY_UPLOADS_ATTACHMENT_COMMENT);
                }
            } catch (err) {
                alert('Error: ' + err + '. Please refresh & try again.');
            }
        },
        [onUploadAsset]
    );

    /**
     * Handle submitting a comment. Calls the parent's onAddComment callback
     * and clears the local editing state on success.
     */
    const handleCommentSubmitButtonClick = useCallback(async () => {
        if (!currentEditingComment) {
            return;
        }
        analyticsService.logEvent(ANALYTICS_EVENTS.USER_ADDS_COMMENT);
        const success = await onAddComment(currentEditingComment);
        if (success) {
            setCurrentEditingComment('');
        }
    }, [currentEditingComment, onAddComment]);

    /**
     * Handle clicking the Comments sidebar tab.
     */
    const handleCommentsTabClick = useCallback(() => {
        setSidebarTab('comments');
    }, []);

    /**
     * Handle clicking the Details sidebar tab.
     */
    const handleDetailsTabClick = useCallback(() => {
        setSidebarTab('details');
    }, []);

    /**
     * Handle closing the error alert bar.
     */
    const handleErrorAlertClose = useCallback(() => {
        setErrorMessage('');
    }, []);

    /**
     * Handle the "Close Case" header button click. Logs an analytics event
     * and opens the close case modal.
     */
    const handleHeaderCloseCaseButtonClick = useCallback(() => {
        analyticsService.logEvent(ANALYTICS_EVENTS.USER_STARTS_CLOSE_CASE_FLOW);
        setShowCloseCaseModal(true);
    }, []);

    /**
     * Handle closing the success alert bar.
     */
    const handleSuccessAlertClose = useCallback(() => {
        setSuccessMessage('');
    }, []);

    /**
     * Handle selecting the "Yes" radio for "Was the vehicle repaired?"
     */
    const handleVehicleRepairedYesChange = useCallback(() => {
        setIsVehicleRepaired(true);
    }, []);

    /**
     * Handle selecting the "No" radio for "Was the vehicle repaired?"
     */
    const handleVehicleRepairedNoChange = useCallback(() => {
        setIsVehicleRepaired(false);
    }, []);

    const { getRootProps, getInputProps } = useDropzone({ onDrop: handleCommentAttachmentDrop });

    return (
        <WorksheetLayout
            header={
                <TechlineHeader
                    caseDetails={caseDetails}
                    caseNumber={caseNumber}
                    disabledDelete
                    disabledSave
                    disabledSubmit
                    disablePrimaryBtn={!allowCloseCase || !hasFailCode || !hasAllWorksheetsSubmitted}
                    onPrimaryBtnClick={handleHeaderCloseCaseButtonClick}
                    submitTooltipText={closeCaseTooltip}
                />
            }
            sidebar={
                <div className={classes.sidebarContainer}>
                    <div className={classes.sidebarTabHeader}>
                        <div className={classes.sidebarTab} onClick={handleCommentsTabClick}>
                            <Typography color={sidebarTab === 'comments' ? 'blueOne' : 'grayFive'} variant="h6">
                                Comments
                            </Typography>
                            <div
                                className={
                                    sidebarTab === 'comments' ? classes.sidebarTabActive : classes.sidebarTabInactive
                                }
                            />
                        </div>
                        <div className={classes.sidebarTab} onClick={handleDetailsTabClick}>
                            <Typography color={sidebarTab === 'details' ? 'blueOne' : 'grayFive'} variant="h6">
                                Details
                            </Typography>
                            <div
                                className={
                                    sidebarTab === 'details' ? classes.sidebarTabActive : classes.sidebarTabInactive
                                }
                            />
                        </div>
                    </div>

                    <div className={classes.sidebarTabContent}>
                        {sidebarTab === 'comments' && (
                            <div>
                                <div className="mt-4">
                                    <Typography color="textDarkSecondary" variant="labelRegular">
                                        Comments submitted through TechShare will be entered into our queue and
                                        responded in the order it is received.
                                    </Typography>
                                </div>

                                <div className="mt-5">
                                    <CaseCommentList comments={commentData} />
                                </div>
                            </div>
                        )}

                        {sidebarTab === 'details' && (
                            <div className="ml-4 mr-4 mt-3">
                                <CaseTeamSection
                                    isAddMemberButtonHidden={isCaseClosed}
                                    isAddMemberDisabled={isCaseClosed}
                                    onAddTeamMember={handleAddTeamMember}
                                    onError={setErrorMessage}
                                    retailerNumber={retailerNumber}
                                    retailerProfiles={caseDetails?.retailerProfiles || []}
                                />
                            </div>
                        )}
                    </div>

                    {sidebarTab === 'comments' && !isCaseClosed && canAddComment && (
                        <div className="ml-3 mr-3">
                            <div className="mb-2 mt-2" style={commentDividerStyle} />
                            <Typography>
                                Comments submitted through TechShare will be entered into our queue and responded in the
                                order it is received.
                            </Typography>
                            <div className="mt-3 bg-white">
                                {canAddOrEditAttachment && (
                                    <div
                                        {...getRootProps()}
                                        className="d-flex justify-content-end mb--10"
                                        style={dropzoneOffsetStyle}
                                    >
                                        <input {...getInputProps()} />
                                        <div style={paperclipStyle}>
                                            <Icon name="paperclip" size={24} />
                                        </div>
                                    </div>
                                )}
                                <TextareaHelper
                                    containerStyle={{ height: 80 }}
                                    onChangeText={setCurrentEditingComment}
                                    placeholder={'Add a comment...'}
                                    value={currentEditingComment}
                                />
                            </div>
                            <div className="d-flex justify-content-end mt-2">
                                {isCommentLoading ? (
                                    <Spinner animation="border" />
                                ) : (
                                    <Button onPress={handleCommentSubmitButtonClick} variant="primary">
                                        Comment
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            }
        >
            {assetErrorMessage && (
                <div>
                    <AlertBar error onClose={onAssetErrorDismiss} show={true}>
                        <Typography>ERROR: {assetErrorMessage}</Typography>
                    </AlertBar>
                </div>
            )}
            {errorMessage && (
                <div>
                    <AlertBar error onClose={handleErrorAlertClose} show={true}>
                        <Typography>ERROR: {errorMessage}</Typography>
                    </AlertBar>
                </div>
            )}
            {successMessage && (
                <div>
                    <AlertBar onClose={handleSuccessAlertClose} show={true} success>
                        <Typography>{successMessage}</Typography>
                    </AlertBar>
                </div>
            )}

            {/* Main content */}
            <div className={classes.container} ref={containerRef}>
                <AssignmentsSection
                    assignmentData={assignmentData}
                    canEditSubmittedCase={canEditSubmittedCase}
                    isCaseClosed={isCaseClosed}
                    isLoadingAssignments={isLoadingAssignments}
                    onRefreshAssignments={handleRefreshAssignments}
                    onToggleAssignmentCompletion={handleToggleAssignmentCompletion}
                />

                {allowFailCodes && (
                    <FailCodeSection
                        canEditSubmittedCase={canEditSubmittedCase}
                        failCodeData={failCodeData}
                        failCodeErrors={failCodeErrors}
                        hasFailCode={hasFailCode}
                        isCaseClosed={isCaseClosed}
                        onClearFailCode={onClearFailCode}
                        onCloseFailCodeModal={onCloseFailCodeModal}
                        onOpenFailCodeModal={onOpenFailCodeModal}
                        onUpdateFailCode={onUpdateFailCode}
                        showFailCodeModal={showFailCodeModal}
                        vehicleVinData={vehicleVinData}
                    />
                )}

                <VehicleInfoSection vehicleVinData={vehicleVinData} />

                <ConcernDetailsSection
                    allowCustomerConcernDetails={allowCustomerConcernDetails}
                    caseDetails={caseDetails}
                />

                <TechDescriptionSection
                    allowDtcCodes={allowDtcCodes}
                    allowTechDescription={allowTechDescription}
                    assetData={assetData}
                    canEditSubmittedCase={canEditSubmittedCase}
                    caseDetails={caseDetails}
                    dtcCodeData={dtcCodeData}
                    isCaseClosed={isCaseClosed}
                    onDeleteAsset={onDeleteAsset}
                    onError={setErrorMessage}
                    onRefreshAssets={onRefreshAssets}
                    onSuccess={setSuccessMessage}
                    onUpdateAsset={onUpdateAsset}
                    onUploadAsset={onUploadAsset}
                />

                <WorksheetsSection
                    allowAdditionalWorksheets={allowAdditionalWorksheets}
                    canEditSubmittedCase={canEditSubmittedCase}
                    hasAllWorksheetsSubmitted={hasAllWorksheetsSubmitted}
                    isCaseClosed={isCaseClosed}
                    isLoadingWorksheets={isLoadingWorksheets}
                    isPilotUser={isPilotUser}
                    onDeleteDraftWorksheetSubmission={onDeleteDraftWorksheetSubmission}
                    onError={setErrorMessage}
                    onFetchWorksheetTitles={onFetchWorksheetTitles}
                    onOpenWorksheet={onOpenWorksheet}
                    onSuccess={setSuccessMessage}
                    worksheetGroupData={worksheetGroupData}
                    worksheetTitleData={worksheetTitleData}
                    worksheetValidationResults={worksheetValidationResults}
                />
            </div>

            {showCloseCaseModal && (
                <Modal centered={true} onHide={handleCloseCaseModalHide} show={showCloseCaseModal} size="lg">
                    <Modal.Header>
                        <Modal.Title>Close case</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className={classes.modalBody}>
                        <Form.Group className="mt-3">
                            <div className="mb-3">
                                <Label required>Was the vehicle repaired?</Label>
                            </div>
                            <Form.Check
                                checked={Boolean(isVehicleRepaired)}
                                className={`${classes.adjustMargin}`}
                                id="customer-report-yes"
                                label={'Yes'}
                                name="customer-report"
                                onChange={handleVehicleRepairedYesChange}
                                type="radio"
                            />
                            <Form.Check
                                checked={isVehicleRepaired === false}
                                className={`${classes.adjustMargin}`}
                                id="customer-report-no"
                                label={'No'}
                                name="customer-report"
                                onChange={handleVehicleRepairedNoChange}
                                type="radio"
                            />
                        </Form.Group>
                        <TextareaHelper
                            containerStyle={{ height: 80 }}
                            label={
                                isVehicleRepaired
                                    ? 'Provide a summary of your findings and actions taken.'
                                    : 'Please provide a detailed explanation of your findings and current status of your vehicle.'
                            }
                            onChangeText={handleCloseDescriptionChange}
                            placeholder={'Write explanation here'}
                            required
                            value={closeDescription}
                        />
                        <div style={closeDescriptionCountStyle}>
                            <Typography color="redOne" variant="labelRegular">
                                {closeDescription.length > 0 && closeDescription.trim() === ''
                                    ? 'Description cannot be empty or just spaces'
                                    : ''}
                            </Typography>
                            <Typography color="textDarkSecondary" variant="labelRegular">
                                {closeDescription.length}/2000
                            </Typography>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <div className="mr-2">
                            <Button onPress={handleCancelCloseCaseButtonClick} variant="ghost-blue">
                                Cancel
                            </Button>
                        </div>

                        <Button
                            disabled={!closeDescription || closeDescription.trim() === '' || closeCaseLoading}
                            isLoading={closeCaseLoading}
                            onPress={handleCloseCaseButtonClick}
                            variant="primary"
                        >
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}
        </WorksheetLayout>
    );
}

const useStyles = createUseStyles({
    adjustMargin: {
        padding: '8px 16px',
    },
    container: {
        padding: '0 24px 16px',
    },
    modalBody: {
        display: 'flex',
        gap: '8px',
        flexDirection: 'column',
    },
    sidebarContainer: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
    },
    sidebarTab: {
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        marginLeft: 16,
        marginTop: 12,
    },
    sidebarTabActive: {
        borderBottom: `2px solid ${colors.blueOne}`,
        marginTop: 12,
    },
    sidebarTabInactive: {
        borderBottom: '2px solid transparent',
        marginTop: 12,
    },
    sidebarTabContent: {
        flex: 1,
        overflowY: 'auto',
    },
    sidebarTabHeader: {
        borderBottom: `1px solid ${colors.grayThree}`,
        display: 'flex',
        flexDirection: 'row',
    },
});
